import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const {
  FiWatch, FiActivity, FiGlobe, FiRadio, FiWifi, FiWifiOff,
  FiBattery, FiBatteryCharging, FiClock, FiShield, FiShieldOff,
  FiCheckCircle, FiChevronDown, FiChevronUp, FiAlertTriangle,
  FiInfo, FiRefreshCw, FiCpu,
} = FiIcons;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PROVIDER_ICONS = {
  apple: FiWatch, garmin: FiActivity, generic_api: FiGlobe,
  fitbit: FiActivity, oura: FiRadio, bluetooth: FiWifi, simulator: FiCpu,
};

const PROVIDER_LABELS = {
  apple: 'Apple Watch', garmin: 'Garmin Connect', generic_api: 'Generic API',
  fitbit: 'Fitbit', oura: 'Oura Ring', bluetooth: 'Bluetooth / BLE', simulator: 'Simulator',
};

const SOURCE_CONFIG = {
  device:    { label: 'Live Device', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', pulse: true },
  simulator: { label: 'Simulated',   cls: 'bg-amber-50 text-amber-700 border-amber-200',     dot: 'bg-amber-500',   pulse: false },
  manual:    { label: 'Manual Entry',cls: 'bg-blue-50 text-blue-700 border-blue-200',         dot: 'bg-blue-400',    pulse: false },
};

const SIGNAL_CONFIG = {
  strong:  { label: 'Strong',   bars: 3, color: 'text-emerald-500' },
  moderate:{ label: 'Moderate', bars: 2, color: 'text-amber-500' },
  weak:    { label: 'Weak',     bars: 1, color: 'text-rose-500' },
  unknown: { label: 'Unknown',  bars: 0, color: 'text-gray-400' },
};

const batteryColor = (b) => {
  if (b === null || b === undefined) return 'text-gray-400';
  if (b > 50) return 'text-emerald-500';
  if (b > 20) return 'text-amber-500';
  return 'text-rose-500';
};

const confidenceColor = (s) => {
  if (s === null || s === undefined) return 'bg-gray-200';
  if (s >= 80) return 'bg-emerald-500';
  if (s >= 50) return 'bg-amber-500';
  return 'bg-rose-500';
};

const confidenceLabel = (s) => {
  if (s === null || s === undefined) return 'Unknown';
  if (s >= 80) return 'High';
  if (s >= 50) return 'Moderate';
  return 'Low';
};

const formatLastSync = (ts) => {
  if (!ts) return 'Never';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─── Signal bars ──────────────────────────────────────────────────────────────
const SignalBars = ({ quality }) => {
  const cfg = SIGNAL_CONFIG[quality] ?? SIGNAL_CONFIG.unknown;
  return (
    <div className="flex items-end gap-0.5">
      {[1, 2, 3].map((bar) => (
        <div key={bar}
          className={`w-1.5 rounded-sm transition-colors ${bar <= cfg.bars
            ? cfg.color.replace('text-', 'bg-') : 'bg-gray-200'}`}
          style={{ height: `${bar * 4 + 4}px` }}
        />
      ))}
    </div>
  );
};

// ─── Source pill ──────────────────────────────────────────────────────────────
const SourcePill = ({ source }) => {
  const cfg = SOURCE_CONFIG[source] ?? SOURCE_CONFIG.manual;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-bold uppercase tracking-wider ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`} />
      {cfg.label}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const DeviceSourcePanel = ({ patient }) => {
  const [expanded, setExpanded]   = useState(false);
  const [device, setDevice]       = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const [loading, setLoading]     = useState(true);

  const fetchDeviceData = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    try {
      // Fetch most recently synced device for this patient
      const { data: deviceData } = await supabase
        .from('devices')
        .select('*')
        .eq('patient_id', patient.id)
        .eq('status', 'active')
        .order('last_sync_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      setDevice(deviceData ?? null);

      // Fetch latest telemetry event for signal quality + confidence score
      if (deviceData?.id) {
        const { data: eventData } = await supabase
          .from('telemetry_events')
          .select('signal_quality, confidence_score, source, received_at')
          .eq('device_id', deviceData.id)
          .order('received_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setLatestEvent(eventData ?? null);
      }
    } catch (err) {
      console.error('[DeviceSourcePanel] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => { fetchDeviceData(); }, [fetchDeviceData]);

  // Reset when patient changes
  useEffect(() => {
    setDevice(null);
    setLatestEvent(null);
    setExpanded(false);
  }, [patient?.id]);

  if (!patient) return null;

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 px-5 py-4 flex items-center gap-3">
        <SafeIcon icon={FiRefreshCw} className="text-gray-400 text-sm animate-spin" />
        <span className="text-xs text-gray-400 font-semibold">Loading device data...</span>
      </div>
    );
  }

  // No device registered
  if (!device) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 px-5 py-4 flex items-center gap-3">
        <SafeIcon icon={FiWifiOff} className="text-gray-400 text-sm" />
        <span className="text-xs text-gray-500 font-semibold">No active device registered for this patient</span>
      </div>
    );
  }

  const ProviderIcon   = PROVIDER_ICONS[device.provider] ?? FiGlobe;
  const providerLabel  = PROVIDER_LABELS[device.provider] ?? device.provider;
  const source         = latestEvent?.source ?? 'device';
  const signalQuality  = latestEvent?.signal_quality ?? 'unknown';
  const confidenceScore = latestEvent?.confidence_score ?? null;
  const isSimulated    = source === 'simulator';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4">
      {/* ── Main row ───────────────────────────────────────────────────────── */}
      <div className="px-5 py-4 flex flex-wrap items-center gap-4">
        {/* Provider identity */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={ProviderIcon} className="text-base text-gray-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{device.device_name}</p>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
              {providerLabel}{device.manufacturer ? ` · ${device.manufacturer}` : ''}
            </p>
          </div>
        </div>

        {/* Source */}
        <SourcePill source={source} />

        {/* Battery */}
        <div className="flex items-center gap-1.5">
          <SafeIcon
            icon={device.battery_level > 20 ? FiBattery : FiBatteryCharging}
            className={`text-base ${batteryColor(device.battery_level)}`}
          />
          <span className="text-xs font-bold text-gray-600">
            {device.battery_level !== null && device.battery_level !== undefined
              ? `${device.battery_level}%` : '—'}
          </span>
        </div>

        {/* Signal */}
        <div className="flex items-center gap-2">
          <SignalBars quality={signalQuality} />
          <span className="text-xs font-semibold text-gray-500">
            {SIGNAL_CONFIG[signalQuality]?.label ?? 'Unknown'}
          </span>
        </div>

        {/* Last sync */}
        <div className="flex items-center gap-1.5">
          <SafeIcon icon={FiClock} className="text-sm text-gray-400" />
          <span className="text-xs font-semibold text-gray-500">
            {formatLastSync(device.last_sync_at)}
          </span>
        </div>

        {/* Expand */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors"
        >
          {expanded ? 'Less' : 'More'}
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-xs" />
        </button>
      </div>

      {/* ── Simulated warning ──────────────────────────────────────────────── */}
      {isSimulated && (
        <div className="px-5 pb-3">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <SafeIcon icon={FiAlertTriangle} className="text-amber-500 text-sm flex-shrink-0 mt-0.5" />
            <p className="text-xs font-medium text-amber-800">
              Readings are from the simulator, not a physical device. Do not use for clinical decisions.
            </p>
          </div>
        </div>
      )}

      {/* ── Expanded detail ────────────────────────────────────────────────── */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {/* Confidence score */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Data Confidence
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${confidenceColor(confidenceScore)}`}
                      style={{ width: `${confidenceScore ?? 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-gray-700 whitespace-nowrap">
                    {confidenceScore ?? '—'}<span className="text-gray-400 font-normal"> / 100</span>
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  {confidenceLabel(confidenceScore)} reliability
                </p>
              </div>

              {/* Firmware */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Firmware</p>
                <code className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded">
                  {device.firmware_version ?? '—'}
                </code>
              </div>

              {/* Source */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Data Source</p>
                <SourcePill source={source} />
              </div>

              {/* Status */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Device Status</p>
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border capitalize ${
                  device.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}>
                  <SafeIcon icon={device.status === 'active' ? FiCheckCircle : FiWifiOff} className="text-xs" />
                  {device.status}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeviceSourcePanel;